import { defineStore } from "pinia";
import { ref } from "vue";

import { apiClient } from "@/client";
import type { Intervention } from "@/type/api";

export const useStoreIntervention = defineStore("intervention", () => {
	// State
	const interventions = ref<Intervention[]>([]);
	const loading = ref(false);
	const error = ref<string | null>(null);

	// Actions
	async function load(): Promise<Intervention[]> {
		loading.value = true;
		error.value = null;
		try {
			const page = await apiClient.JSONGet<Intervention[]>("/api/intervention");
			interventions.value = page;
			return page;
		} catch (err) {
			error.value =
				err instanceof Error ? err.message : "Failed to load interventions";
			throw err;
		} finally {
			loading.value = false;
		}
	}

	// Create is find-or-create by name, so a duplicate name returns the
	// existing row rather than failing; either way the list is reloaded.
	async function create(
		name: string,
		controlClass: string | null = null,
	): Promise<Intervention> {
		error.value = null;
		try {
			const created = await apiClient.JSONPost<Intervention>(
				"/api/intervention",
				{ name, control_class: controlClass },
			);
			await load();
			return created;
		} catch (err) {
			error.value =
				err instanceof Error ? err.message : "Failed to save intervention";
			throw err;
		}
	}

	// A retire is soft: the row stays on past treatments, and the list is
	// reloaded to show it as retired.
	async function retire(id: number): Promise<void> {
		error.value = null;
		try {
			await apiClient.JSONDelete(`/api/intervention/${id}`);
			await load();
		} catch (err) {
			error.value =
				err instanceof Error ? err.message : "Failed to retire intervention";
			throw err;
		}
	}

	// Update sets one intervention's planned residual (operator override) and
	// reloads the list so the row reflects what the server stored; the server
	// re-derives every treatment that recorded it. A residualDays of null
	// clears the district's planned residual.
	async function update(
		id: number,
		residualDays: number | null,
	): Promise<void> {
		error.value = null;
		try {
			await apiClient.JSONPut(`/api/intervention/${id}`, {
				residual_days: residualDays,
			});
			await load();
		} catch (err) {
			error.value =
				err instanceof Error ? err.message : "Failed to update intervention";
			throw err;
		}
	}

	return {
		// State
		interventions,
		// Actions
		load,
		create,
		retire,
		update,
		loading,
		error,
	};
});
